import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClipStatus } from '../hooks/useClipStatus';
import { API_URL } from '../Config';

function TimelineItem({ item, itemIndex, onConfirmRemoveItem, onUpdateText, onEditClip, onPreviewFrame, videoUrl, onMoveUp, onMoveDown, onUpdateSceneDescription, onUpdateSceneNarration, onRegenerateAudio, onRegenScript, setIsLoading, setMessage, selectedLanguage, projectId, onDuplicateItem }) {
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);
  const [localRawText, setLocalRawText] = useState(item.rawText || '');
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const clipStatus = useClipStatus(item.id, selectedLanguage);
  const displayNarration = selectedLanguage === 'original'
    ? item.scene_narration || ''
    : (item.translations && item.translations.find(t => t.language_code === selectedLanguage)?.translated_text) || '';

  const [localNarrationText, setLocalNarrationText] = useState(displayNarration);

  // Check if running on localhost to hide certain buttons
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  useEffect(() => {
    setLocalNarrationText(displayNarration);
  }, [displayNarration, selectedLanguage]);

  // Sync local text with item.rawText when it changes externally
  useEffect(() => {
    setLocalRawText(item.rawText || '');
  }, [item.rawText]);

  // For audio, the backend updates scene_audio field with language-specific filename
  // But for display consistency, we should check if there's a language-specific path available
  const audioSource = selectedLanguage === 'original'
    ? item.scene_audio
    : (item.translations && item.translations.find(t => t.language_code === selectedLanguage)?.translated_audio_path);

  // Debounced update function
  const debouncedUpdate = useCallback((id, text) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      onUpdateText(id, text);
    }, 300); // 300ms delay after user stops typing
  }, [onUpdateText]);

  // Force immediate save of pending changes
  const flushPendingUpdates = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      onUpdateText(item.id, localRawText);
    }
  }, [item.id, localRawText, onUpdateText]);

  // Handle text changes with immediate UI update and debounced API call
  const handleTextChange = (e) => {
    const textarea = e.target;
    const newValue = e.target.value;
    const cursorPosition = textarea.selectionStart;
    
    // Update local state immediately for responsive UI
    setLocalRawText(newValue);
    
    // Store cursor position for restoration
    cursorPositionRef.current = cursorPosition;
    
    // Debounced API call
    debouncedUpdate(item.id, newValue);
  };

  // Handle blur event to immediately save pending changes
  const handleTextBlur = () => {
    flushPendingUpdates();
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowVoiceDropdown(false);
      }
    };

    if (showVoiceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVoiceDropdown]);

  // Restore cursor position after re-render
  useEffect(() => {
    if (textareaRef.current && cursorPositionRef.current !== null) {
      const textarea = textareaRef.current;
      const position = cursorPositionRef.current;
      textarea.setSelectionRange(position, position);
      cursorPositionRef.current = null;
    }
  });

  const handleGenerateClick = (variation = 1) => {
    // This function will now be passed from the parent `VideoEditorPage`
    // It will need to know the item.id, selectedLanguage, and the chosen variation.
    onRegenerateAudio(item.id, variation);
    setShowVoiceDropdown(false); // Close dropdown after selection
  };

  // Voice variations configuration
  const voiceVariations = [
    { id: 1, label: 'Variation 1', description: 'Neutral delivery' },
    { id: 2, label: 'Variation 2', description: 'Energetic & expressive' },
    { id: 3, label: 'Variation 3', description: 'Dramatic & engaging' }
  ];

  return (
    <div className="timeline-item">
      <div className="timeline-item-header">
        <strong>
          {item.type === 'frame' && `Frame ${itemIndex} at ${item.timestamp && Number.isFinite(item.timestamp) ? item.timestamp.toFixed(2) : '0.00'}s`}
          {item.type === 'clip' && `Clip ${itemIndex} from ${item.startTime && Number.isFinite(item.startTime) ? item.startTime.toFixed(2) : '0.00'}s to ${item.endTime && Number.isFinite(item.endTime) ? item.endTime.toFixed(2) : '0.00'}s`}
          {item.type === 'intro' && `Intro ${itemIndex}`}
          {item.type === 'outro' && `Outro ${itemIndex}`}
        </strong>
        <div className="timeline-item-controls">
          {!isLocalhost && (item.type === 'clip' || item.type === 'intro' || item.type === 'outro') && (
            <button onClick={() => navigate(`/project/${projectId}/clip-editor/${item.id}`)} className={`clip-editor-button clip-status-${clipStatus}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-scissors" viewBox="0 0 16 16">
                <path d="M3.5 3.5c-.614-.884-.074-1.962.858-2.5L8 7.226 11.642 1c.932.538 1.472 1.616.858 2.5L8.81 8.61l1.556 2.661c.372.637.068 1.434-.6 1.756a1.102 1.102 0 0 1-1.444-.392L8.125 12.4 8 12.027L7.875 12.4l-.197.235a1.102 1.102 0 0 1-1.444.392c-.668-.322-.972-1.119-.6-1.756L7.19 8.61 3.5 3.5zm2.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm7 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/>
              </svg>
            </button>
          )}
          {!isLocalhost && (item.type === 'frame' || item.type === 'clip') && item.screenshot_path && (
            <button onClick={() => navigate(`/project/${projectId}/frame-editor/${item.id}`)} className="edit-frame-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil" viewBox="0 0 16 16">
                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708L14.5 5.207l-3-3L12.854.146a.5.5 0 0 1 .708 0zm-.646 4.5L14.207 7 8 13.207 5.5 13.5 5.793 11 12.146 4.646zm-2.793-1L6.854 6.354 1.5 11.707V13.5h1.793l5.354-5.354-2.793-2.792z"/>
              </svg>
            </button>
          )}
          {!isLocalhost && item.type !== 'intro' && item.type !== 'outro' && (
            <>
              <button onClick={() => onMoveUp(item.id)} className="move-item-button">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"/>
                </svg>
              </button>
              <button onClick={() => onMoveDown(item.id)} className="move-item-button">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-down" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"/>
                </svg>
              </button>
              <button onClick={() => onDuplicateItem(item.id)} className="duplicate-item-button" title="Duplicate this timeline item">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-files" viewBox="0 0 16 16">
                  <path d="M13 0H6a2 2 0 0 0-2 2 2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 13V4a2 2 0 0 0-2-2H5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1zM3 4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4z"/>
                </svg>
              </button>
            </>
          )}
          {!isLocalhost && <button onClick={() => onConfirmRemoveItem(item)} className="remove-item-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
            </svg>
          </button>}
        </div>
      </div>
      <div className="timeline-item-content">
        <div className="timeline-media-container">
          {item.type === 'frame' && item.screenshot_path && (
            <>
              <img src={`${API_URL}${item.screenshot_path}`} alt="Frame Screenshot" className="timeline-media" />
              {item.annotated_image_path && (
                <button onClick={() => onPreviewFrame(item)} className="frame-preview-button">Frame</button>
              )}
            </>
          )}
          {item.type === 'clip' && item.screenshot_path && (
            <>
              <img src={`${API_URL}${item.screenshot_path}`} alt="Clip Screenshot" className="timeline-media" onClick={() => onEditClip(item)} />
              {item.annotated_image_path && (
                <button onClick={() => onPreviewFrame(item)} className="frame-preview-button">Frame</button>
              )}
              {!isLocalhost && <button onClick={() => onEditClip(item)} className="edit-clip-button">Edit Clip</button>}
            </>
          )}
        </div>
        <textarea
          ref={textareaRef}
          value={localRawText}
          onChange={isLocalhost ? null : handleTextChange}
          onBlur={isLocalhost ? null : handleTextBlur}
          placeholder="Describe what's happening..."
          rows="3"
          className="timeline-item-textarea"
          readOnly={isLocalhost}
        />
      </div>
      <button onClick={() => setShowMore(!showMore)} className="show-more-button">
        {showMore ? 'Show Less' : 'Show More'}
      </button>
      {showMore && (
        <div className="timeline-item-accordion-content">
          <div className="scene-description-edit">
            <label>Scene Description:</label>
            <textarea
              value={item.scene_description || ''}
              onChange={isLocalhost ? null : (e) => onUpdateSceneDescription(item.id, e.target.value)}
              placeholder="AI generated description..."
              rows="3"
              readOnly={isLocalhost}
            />
          </div>
          <div className="scene-narration-edit">
            <label>Scene Narration:</label>
            <textarea
              value={localNarrationText}
              onChange={isLocalhost ? null : (e) => setLocalNarrationText(e.target.value)}
              onBlur={isLocalhost ? null : (e) => onUpdateSceneNarration(item.id, e.target.value, selectedLanguage)}
              placeholder="AI generated narration script..."
              rows="3"
              readOnly={isLocalhost}
            />
          </div>
          <div className="scene-audio-section">
            <label>Scene Audio:</label>
            <div className="audio-controls-container">
              <div className="audio-player">
                {audioSource ? (
                  <audio controls src={`${API_URL}${audioSource}`} type="audio/mpeg">
                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <p>No audio generated yet.</p>
                )}
              </div>
              {!isLocalhost && (
                <div className="voice-generation-dropdown" ref={dropdownRef}>
                  <button
                    onClick={() => setShowVoiceDropdown(!showVoiceDropdown)}
                    className="regenerate-audio-button dropdown-toggle"
                  >
                    {audioSource ? 'Regenerate Voice' : 'Generate Voice'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16" style={{marginLeft: '5px'}}>
                      <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                    </svg>
                  </button>

                  {showVoiceDropdown && (
                    <div className="voice-dropdown-menu">
                      {voiceVariations.map((variation) => (
                        <button
                          key={variation.id}
                          onClick={() => handleGenerateClick(variation.id)}
                          className="voice-variation-option"
                        >
                          <div className="variation-label">{variation.label}</div>
                          <div className="variation-description">{variation.description}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!isLocalhost && <button onClick={() => onRegenScript(item.id)} className="regenerate-script-button">
                Re-gen Script
              </button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimelineItem;